'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ScanSearch,
  Sparkles,
  CalendarClock,
  Droplets,
  ShieldCheck,
  Leaf,
  Sprout,
  Users,
  ChevronRight,
  FileText,
  X, // <-- Imported X icon for the modal close button
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';

/* ──────────────────────────────────────────────────────────────────────────
   SINGLE SOURCE OF TRUTH
   Slide/feature text is keyed by index and pulled from i18n at render time.
   ────────────────────────────────────────────────────────────────────────── */

const SLIDES = [
  {
    counter: '01',
    icon: ScanSearch,
    mobileImage: '/assets/fear_section/mobile_slides_1.jpeg',
    desktopImage: '/assets/fear_section/vertical_slides_1.png',
    fallback: 'linear-gradient(135deg,#d14124 0%,#5a1409 100%)',
  },
  {
    counter: '02',
    icon: Sparkles,
    mobileImage: '/assets/fear_section/mobile_slides_2.jpeg',
    desktopImage: '/assets/fear_section/vertical_slides_2.jpeg',
    fallback: 'linear-gradient(135deg,#b08a5e 0%,#4a3520 100%)',
  },
  {
    counter: '03',
    icon: CalendarClock,
    mobileImage: '/assets/fear_section/mobile_slides_3.jpeg',
    desktopImage: '/assets/fear_section/vertical_slide_3.jpeg',
    fallback: 'linear-gradient(135deg,#87a96b 0%,#2f4a28 100%)',
  },
  {
    counter: '04',
    icon: Droplets,
    mobileImage: '/assets/fear_section/mobile_slides_4.jpeg',
    desktopImage: '/assets/fear_section/vertical_slides_4.jpeg',
    fallback: 'linear-gradient(135deg,#5a7a4f 0%,#1f2e1a 100%)',
  },
  {
    counter: '05',
    icon: Droplets,
    mobileImage: '/assets/fear_section/mobile_slides_5.jpeg',
    desktopImage: '/assets/fear_section/vertical_slides_5.jpeg',
    fallback: 'linear-gradient(135deg,#5a7a4f 0%,#1f2e1a 100%)',
  },
];

const FEATURES = [{ icon: ShieldCheck }, { icon: Leaf }, { icon: Sprout }, { icon: Users }];

const SLIDE_DURATION = 5000; // ms

/* ──────────────────────────────────────────────────────────────────────────
   COMPONENT
   ────────────────────────────────────────────────────────────────────────── */

export default function DrDetoxerFearSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imgErr, setImgErr] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false); // <-- New state for modal
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const { t } = useTranslation('home');
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  /* ---- Auto-advance with real-time progress ---- */
  useEffect(() => {
    // Pause auto-advance if hovered or if the modal is currently open
    if (paused || isModalOpen) return;
    startRef.current = performance.now();
    const tick = (now) => {
      const elapsed = now - startRef.current;
      const p = Math.min(elapsed / SLIDE_DURATION, 1);
      setProgress(p);
      if (p >= 1) setActiveIndex((i) => (i + 1) % SLIDES.length);
      else rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [activeIndex, paused, isModalOpen]);

  /* ---- Lock body scroll when modal is open ---- */
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isModalOpen]);

  const goTo = useCallback((i) => {
    setActiveIndex(i);
    setProgress(0);
  }, []);
  const next = () => goTo((activeIndex + 1) % SLIDES.length);

  /* ---- 3D stack transforms (your doc's formula, wrap-around aware) ---- */
  const stackStyle = (i) => {
    const n = SLIDES.length;
    let d = i - activeIndex;
    if (d > n / 2) d -= n;
    if (d < -n / 2) d += n;

    if (d === 0)
      return { transform: 'translateX(0) scale(1)', opacity: 1, zIndex: 30, filter: 'blur(0)' };
    if (d === -1)
      return {
        transform: 'translateX(-28%) scale(0.78)',
        opacity: 0.55,
        zIndex: 20,
        filter: 'blur(2px)',
      };
    if (d === 1)
      return {
        transform: 'translateX(28%) scale(0.78)',
        opacity: 0.55,
        zIndex: 20,
        filter: 'blur(2px)',
      };
    // far edges (absDiff === 2 for 4-slide carousel)
    return {
      transform: `translateX(${d > 0 ? 52 : -52}%) scale(0.68)`,
      opacity: 0.38,
      zIndex: 10,
      filter: 'blur(4px)',
    };
  };

  const active = SLIDES[activeIndex];
  const ActiveIcon = active.icon;
  const activeTitle = t(`problem.fear.slides.${activeIndex}.title`);
  const activeDesc = t(`problem.fear.slides.${activeIndex}.desc`);

  const handleBuyNow = () => {
    document.getElementById('product')?.scrollIntoView({ behavior: 'smooth' });
    // Assuming setMenuOpen is handled elsewhere or is a prop, left as is:
    if (typeof setMenuOpen === 'function') setMenuOpen(false);
  };

  const downloadReport = (reportName) => {
    const link = document.createElement('a');
    link.href = `/assets/reports/${reportName}.pdf`;
    link.download = `${reportName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCardClick = (i, isActive) => {
    if (isActive) {
      if (!imgErr[i]) setIsModalOpen(true); // Open modal if active and image is not broken
    } else {
      goTo(i);
    }
  };

  return (
    <div className="font-body min-h-screen w-full bg-[#fafaf6] text-[#0b2c4a] antialiased">
      <div ref={ref}>
        {/* ---------- STYLES: fonts + keyframes ---------- */}
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
        .font-body { font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif; }

        @keyframes fadeUp {
          0%   { opacity: 0; transform: translateY(18px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        .anim-fadeUp { animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .anim-fadeIn { animation: fadeIn 0.6s ease-out both; }

        /* Slide transitions — single easing across the whole system */
        .stack-card {
          transition:
            transform 700ms cubic-bezier(0.22, 1, 0.36, 1),
            opacity   700ms cubic-bezier(0.22, 1, 0.36, 1),
            filter    700ms cubic-bezier(0.22, 1, 0.36, 1);
        }
      `}</style>

        <section className="mx-auto max-w-[1400px] px-5 py-5 sm:px-8 sm:py-14 lg:px-10 lg:py-20 overflow-hidden sm:overflow-visible">
          <div
            className="grid items-center gap-2 lg:grid-cols-12 lg:gap-12"
            onMouseLeave={() => setPaused(false)}
          >
            {/* ═══════════════════ LEFT: 3D STACKED CAROUSEL ═══════════════════ */}
            <div className="order-1 lg:col-span-6">
              <div className="relative mx-auto aspect-[4.5/5] sm:aspect-[4/5] w-full max-w-[560px] overflow-hidden sm:overflow-visible">
                <div className="absolute inset-0 flex items-center justify-center">
                  {SLIDES.map((s, i) => {
                    const style = stackStyle(i);
                    const isActive = i === activeIndex;
                    const slideTitle = t(`problem.fear.slides.${i}.title`);
                    return (
                      <div
                        key={i}
                        onClick={() => handleCardClick(i, isActive)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleCardClick(i, isActive);
                          }
                        }}
                        className="stack-card absolute h-[65%] sm:h-[88%] w-[70%] cursor-pointer select-none overflow-hidden rounded-[26px] shadow-[0_24px_60px_-20px_rgba(11,44,74,0.35)] ring-1 ring-black/5"
                        style={{ ...style, background: s.fallback }}
                        aria-label={t('problem.fear.aria.slide', { num: i + 1, title: slideTitle })}
                        aria-hidden={!isActive}
                      >
                        {!imgErr[i] && (
                          <picture>
                            <source media="(min-width: 640px)" srcSet={s.desktopImage} />
                            <img
                              src={s.mobileImage}
                              alt=""
                              draggable={false}
                              onError={() => setImgErr((e) => ({ ...e, [i]: true }))}
                              className="h-full w-full  transition-transform duration-500 hover:scale-105"
                            />
                          </picture>
                        )}
                        {/* editorial tint on the active card only */}
                        {isActive && (
                          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0b2c4a]/15" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dot indicators under the stack */}
              <div className="sm:mt-7 flex justify-center gap-2">
                {SLIDES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    aria-label={t('problem.fear.aria.goToSlide', { num: i + 1 })}
                    className={`h-2 rounded-full transition-all duration-500 ${
                      i === activeIndex
                        ? 'w-8 bg-[#16803c]'
                        : 'w-2 bg-[#0b2c4a]/20 hover:bg-[#0b2c4a]/40'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* ═══════════════════ RIGHT: TEXT COLUMN ═══════════════════ */}
            <div className="order-2 space-y-6 lg:col-span-6 lg:space-y-7">
              {/* 1. Top tag */}
              <div className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#16803c]/25 bg-[#e7f6ec] px-3.5 py-1.5 text-[10.5px] font-700 uppercase tracking-[0.22em] text-[#16803c]">
                <Leaf className="h-3.5 w-3.5" strokeWidth={2.5} fill="#16803c" />
                {t('problem.fear.tag')}
              </div>

              {/* 2. Big headline — green + navy split */}
              <h2 className="text-[34px] font-800 leading-[1.06] tracking-[-0.02em] text-[#0b2c4a] sm:text-[42px] lg:text-[52px]">
                {t('problem.fear.headline.line1')}
                <br />
                <span className="text-[#16803c]">{t('problem.fear.headline.emphasis')}</span>{' '}
                <span className="text-[#0b2c4a]">{t('problem.fear.headline.tail')}</span>
              </h2>

              {/* 3. Gold divider */}
              <div className="hidden sm:block h-[2px] w-16 rounded-full bg-[#c8a86b]" />

              {/* 6. ANIMATED FEAR CARD */}
              <div className="relative overflow-hidden rounded-[22px] bg-white p-5 shadow-[0_14px_40px_-18px_rgba(11,44,74,0.22)] ring-1 ring-[#0b2c4a]/6 sm:p-6 lg:p-7">
                <div className="flex items-start gap-4 sm:gap-5">
                  {/* Icon circle */}
                  <div
                    key={`icon-${activeIndex}`}
                    className="anim-fadeIn flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-[#e7f6ec] ring-1 ring-[#16803c]/10 sm:h-16 sm:w-16"
                  >
                    <ActiveIcon
                      className="h-7 w-7 text-[#16803c] sm:h-8 sm:w-8"
                      strokeWidth={1.75}
                    />
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="mb-1.5 text-[13px] font-600">
                      <span className="text-[#16803c]">{active.counter}</span>
                      <span className="text-[#94a3b8]">
                        {' '}
                        / {String(SLIDES.length).padStart(2, '0')}
                      </span>
                    </div>

                    <h3
                      key={`title-${activeIndex}`}
                      className="anim-fadeUp text-[19px] font-800 leading-[1.2] tracking-[-0.01em] text-[#0b2c4a] sm:text-[22px]"
                    >
                      {activeTitle}
                    </h3>

                    <p
                      key={`desc-${activeIndex}`}
                      className="anim-fadeUp mt-3 text-[13.5px] leading-[1.65] text-[#64748b] sm:text-[14px]"
                      style={{ animationDelay: '150ms' }}
                    >
                      {activeDesc}
                    </p>
                  </div>

                  {/* Arrow button */}
                  <button
                    onClick={next}
                    aria-label={t('problem.fear.aria.nextSlide')}
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#16803c] text-white shadow-md transition hover:bg-[#0f6b30] active:scale-95 sm:h-11 sm:w-11"
                  >
                    <ChevronRight className="h-5 w-5" strokeWidth={2.5} />
                  </button>
                </div>

                {/* Progress bars */}
                <div className="mt-5 flex gap-1.5">
                  {SLIDES.map((_, i) => {
                    const fill = i < activeIndex ? 1 : i === activeIndex ? progress : 0;
                    return (
                      <button
                        key={i}
                        onClick={() => goTo(i)}
                        aria-label={t('problem.fear.aria.goToSlide', { num: i + 1 })}
                        className="relative h-[3px] flex-1 overflow-hidden rounded-full bg-[#e2e8f0] transition hover:bg-[#cbd5e1]"
                      >
                        <span
                          className="absolute inset-y-0 left-0 rounded-full bg-[#16803c]"
                          style={{
                            width: `${fill * 100}%`,
                            transition:
                              i === activeIndex && !paused && !isModalOpen
                                ? 'none'
                                : 'width 300ms ease',
                          }}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 7. Feature strip */}
              <div className="grid grid-cols-2 gap-3 rounded-[18px] bg-gradient-to-br from-[#e7f6ec] to-[#f0faf3] p-4 sm:grid-cols-4 sm:gap-3 sm:p-5">
                {FEATURES.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-[#16803c]/10">
                        <Icon className="h-4 w-4 text-[#16803c]" strokeWidth={2} />
                      </div>
                      <div className="text-[10.5px] font-600 leading-[1.25] text-[#0b2c4a] sm:text-[11px]">
                        {t(`problem.fear.features.${i}.line1`)}
                        <br />
                        {t(`problem.fear.features.${i}.line2`)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 8 & 9. CTA + subtitle */}
              <div className="space-y-3 pt-1">
                <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-evenly sm:gap-2">
                  <button
                    onClick={() => handleBuyNow()}
                    className="flex sm:flex-row gap-4 mx-auto btn-primary px-7 py-3 rounded-xl text-sm font-semibold shadow-green"
                    aria-label={t('problem.fear.aria.buyNow')}
                  >
                    <ShieldCheck
                      className="h-5 w-5 text-[#3fd67a] transition group-hover:text-[#6ee89e]"
                      strokeWidth={2.5}
                    />{' '}
                    {t('common:actions.buyNow')}
                  </button>

                  <button
                    onClick={() => downloadReport('LILABА')}
                    className="flex sm:flex-row gap-4 mx-auto btn-primary px-7 py-3 rounded-xl text-sm font-semibold shadow-green"
                    aria-label={t('problem.fear.aria.buyNow')}
                  >
                    <FileText
                      className="h-5 w-5 text-[#3fd67a] transition group-hover:text-[#6ee89e]"
                      strokeWidth={2.5}
                    />{' '}
                    Toxicity Reports
                  </button>
                  <button
                    onClick={() => downloadReport('PLPL')}
                    className="flex sm:flex-row gap-4 mx-auto btn-primary px-7 py-3 rounded-xl text-sm font-semibold shadow-green"
                    aria-label={t('problem.fear.aria.buyNow')}
                  >
                    <FileText
                      className="h-5 w-5 text-[#3fd67a] transition group-hover:text-[#6ee89e]"
                      strokeWidth={2.5}
                    />{' '}
                    Effectiveness Reports
                  </button>
                </div>

                <p className="text-center text-[13px] text-[#64748b]">
                  {t('problem.fear.footer.prefix')}{' '}
                  <span className="font-700 text-[#0b2c4a]">{t('problem.fear.footer.brand')}</span>
                  {t('problem.fear.footer.suffix')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ IMAGE MODAL DIALOG ═══════════════════ */}
        {isModalOpen && !imgErr[activeIndex] && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm anim-fadeIn"
            onClick={() => setIsModalOpen(false)}
            role="dialog"
            aria-modal="true"
          >
            {/* Close button - Fixed to top right of screen for easy access */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute right-4 top-4 z-[110] flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/50 sm:right-8 sm:top-8"
              aria-label="Close modal"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Modal Content - e.stopPropagation() prevents the backdrop click from closing it when clicking the image */}
            <div
              className="relative flex max-h-full max-w-full items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <picture>
                <source
                  media="(min-width: 640px)"
                  srcSet={SLIDES[activeIndex].desktopImage}
                />
                <img
                  src={SLIDES[activeIndex].mobileImage}
                  alt="Expanded view"
                  className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain shadow-2xl"
                />
              </picture>
            </div>
          </div>
        )}
      </div>
      {/* Solution bridge */}
      <motion.div
        className="mt-16 lg:mt-12 pb-8 text-center px-4"
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, delay: 0.7 }}
      >
        <p className="text-muted text-xs lg:text-sm tracking-widest uppercase mb-3 lg:mb-4">
          {t('problem.solutionLabel')}
        </p>
        <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light italic text-foreground leading-tight">
          {t('problem.solutionTitle')} <br className="block sm:hidden" />
          <span className="text-gradient-green not-italic font-semibold">
            {t('problem.solutionHighlight')}
          </span>
        </h3>
      </motion.div>
    </div>
  );
}
